import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { CircleCheck } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import { GatewayActions, GatewayBrand, GatewayContent, GatewayShell } from '@/_shared/components/GatewayShell';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/_shared/components/ui/form';
import { Input } from '@/_shared/components/ui/input';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';

import { LinkNotValid } from '../components/nativeSignIn/LinkNotValid';
import { RequireNativeSignIn } from '../components/nativeSignIn/RequireNativeSignIn';
import { TextLink } from '../components/nativeSignIn/TextLink';
import { useLinkParams } from '../hooks/useLinkParams';
import { PASSWORD_RULE_HINT, resetPasswordSchema, type ResetPasswordValues } from '../lib/authFormSchema';
import { setNewPassword } from '../lib/rudderstack-auth-client';
import { webAppResetLink } from '../lib/web-app-links';

/** `/reset-password?token=…`: the page the password-reset email links to. */
export function ResetPassword(): React.ReactElement {
  usePageTitle('Reset password');
  // First, so the token leaves the URL before anything records it.
  const token = useLinkParams().get('token');

  return (
    <RequireNativeSignIn webAppLink={(webAppUrl) => (token ? webAppResetLink(webAppUrl, token) : null)}>
      {(config) =>
        token ? (
          <ResetPasswordForm apiBaseUrl={config.apiBaseUrl} token={token} />
        ) : (
          <LinkNotValid actionLabel="Request a new link" actionTo="/forgot-password" />
        )
      }
    </RequireNativeSignIn>
  );
}

function ResetPasswordForm({ apiBaseUrl, token }: { apiBaseUrl: string; token: string }): React.ReactElement {
  const form = useForm<ResetPasswordValues>({
    resolver: zodResolver(resetPasswordSchema),
    mode: 'onTouched',
    defaultValues: { password: '', confirmPassword: '' },
  });
  const update = useMutation({
    mutationFn: (password: string) => setNewPassword(apiBaseUrl, { token, password }),
    gcTime: 0,
  });

  if (update.isSuccess) {
    return (
      <GatewayShell>
        <GatewayBrand poweredBy />
        <GatewayContent
          icon={<CircleCheck />}
          variant="success"
          title="Password updated"
          description="Sign in with your new password."
        />
        <GatewayActions>
          <Button variant="primary" size="lg" className="w-full" asChild>
            <Link to="/login">Sign in</Link>
          </Button>
        </GatewayActions>
      </GatewayShell>
    );
  }

  return (
    <GatewayShell>
      <GatewayBrand poweredBy />
      <GatewayContent title="Set a new password">
        <Form {...form}>
          <form
            noValidate
            aria-label="Set a new password"
            className="flex flex-col gap-4 text-left"
            onSubmit={form.handleSubmit((values) => update.mutate(values.password))}
          >
            {update.error && (
              <Callout variant="error" role="alert">
                {update.error.message}
              </Callout>
            )}
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New password</FormLabel>
                  <FormControl>
                    <Input {...field} type="password" size="lg" autoComplete="new-password" />
                  </FormControl>
                  <FormDescription className="text-xs">{PASSWORD_RULE_HINT}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm password</FormLabel>
                  <FormControl>
                    <Input {...field} type="password" size="lg" autoComplete="new-password" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" variant="primary" size="lg" className="w-full" loading={update.isPending}>
              {update.isPending ? 'Updating…' : 'Update password'}
            </Button>
          </form>
        </Form>
        {/* An expired link reaches the form too, and the refusal text alone gives no way on. */}
        <p className="mt-4 text-xs">
          <TextLink to="/forgot-password" tone="quiet">
            Request a new link
          </TextLink>
        </p>
      </GatewayContent>
    </GatewayShell>
  );
}
